import React from 'react';
import { X } from 'lucide-react';
import { C } from '../../theme.js';
import { Button, Eyebrow, IconButton, TextInput } from '../../components/Primitives.jsx';
import { ColSelect } from '../shared/ColSelect.jsx';

// ── Column ops ─────────────────────────────────────────────────────────
export function RenameInspector({ step, onChange, columns }) {
  const c = step.config || {};
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  // Draft rows live locally: a half-filled row (source picked, new name not
  // yet) isn't a valid rename, so it can't round-trip through config without
  // being dropped mid-typing. Only complete pairs are committed.
  const committed = Object.entries(c.renames || {});
  const [rows, setRows] = React.useState(committed.length ? committed : [['', '']]);
  const lastCommitted = React.useRef(JSON.stringify(committed));
  React.useEffect(() => {
    // Adopt external changes (different step selected, undo) but not our own commits.
    const now = JSON.stringify(committed);
    if (now !== lastCommitted.current) {
      lastCommitted.current = now;
      setRows(committed.length ? committed : [['', '']]);
    }
  }, [step.id, c.renames]); // eslint-disable-line react-hooks/exhaustive-deps
  const commit = (next) => {
    setRows(next.length ? next : [['', '']]);
    const renames = Object.fromEntries(next.filter(([k, v]) => k && v));
    lastCommitted.current = JSON.stringify(Object.entries(renames));
    u({ renames });
  };
  const updateSource = (i, v) => commit(rows.map((r, j) => (j === i ? [v, r[1]] : r)));
  const updateName = (i, v) => commit(rows.map((r, j) => (j === i ? [r[0], v] : r)));
  const removeRow = (i) => commit(rows.filter((_, j) => j !== i));
  const addRow = () => commit([...rows, ['', '']]);
  return (
    <div>
      <Eyebrow>Rename columns</Eyebrow>
      <div style={{ color: C.fg4, fontSize: '.78rem', margin: '4px 0 10px' }}>
        Pick a source column, then type its new name.
      </div>
      {rows.map(([src, name], i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
          <ColSelect value={src} columns={columns} onChange={(v) => updateSource(i, v)}
            placeholder="— source column —" style={{ flex: 1 }} />
          <span style={{ color: C.fg5, fontSize: '.8rem' }}>→</span>
          <TextInput value={name} onChange={(v) => updateName(i, v)} placeholder="new name" mono style={{ flex: 1 }} />
          <IconButton title="Remove" onClick={() => removeRow(i)}><X size={14} /></IconButton>
        </div>
      ))}
      <Button kind="ghost" size="sm" onClick={addRow} icon="＋">Add rename</Button>
    </div>
  );
}
